import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MSAL_INSTANCE } from '@azure/msal-angular';
import { AccountInfo, PublicClientApplication, SilentRequest } from '@azure/msal-browser';
import { AppInitService } from '../services/app-init.service';
import { catchError, defer, finalize, of, switchMap, tap, throwError } from 'rxjs';
import { SpinnerService } from '../services/spinner.service';
import { environment } from '../../../../../environments/environment';

export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  const msalInstance = inject<PublicClientApplication>(MSAL_INSTANCE);
  const appInitService = inject(AppInitService);
  const spinnerService = inject(SpinnerService);

  let api: 'engtool' | 'iotest' | null = null;
  const isFormData = req.body instanceof FormData;
  if (environment.name.includes('cloud') && req.method !== 'OPTIONS') {
    try {
      const urlOrigin = new URL(req.url, window.location.origin).origin;
      if (urlOrigin === new URL(environment.engtoolapi).origin) {
        api = 'engtool';
      } else if (urlOrigin === new URL(environment.iotestapi).origin) {
        api = 'iotest';
      }
    } catch (error) {
      console.warn('Error parsing URL on the interceptor:', error);
    }
  }

  const tokenKey = api === 'engtool' ? 'msal_access_token' : api === 'iotest' ? 'access_token' : null;
  const authorizeRequest = () => {
    if (!tokenKey) {
      return req;
    }

    const token = sessionStorage.getItem(tokenKey) || '';
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        Accept: api === 'engtool' ? 'application/json' : '*/*',
        ...(api === 'engtool' && !isFormData ? { 'Content-Type': 'application/json; charset=utf-8' } : {}),
      },
    });
  };

  return defer(() => {
    spinnerService.show();
    return defer(() => next(authorizeRequest())).pipe(
      catchError((err: HttpErrorResponse) => {
        if (!tokenKey || err.status !== 401) {
          return throwError(() => err);
        }

        let account: AccountInfo | null;
        try {
          const accountStr = sessionStorage.getItem('account');
          account = accountStr ? JSON.parse(decodeURIComponent(accountStr)) : msalInstance.getActiveAccount();
        } catch {
          return throwError(() => err);
        }
        if (!account) {
          return throwError(() => err);
        }

        const silentRequest: SilentRequest = {
          account,
          scopes: environment.apiScopes,
          forceRefresh: true,
        };

        return defer(() => msalInstance.acquireTokenSilent(silentRequest)).pipe(
          tap(tokenResponse => {
            if (!tokenResponse.accessToken) {
              throw new Error('No Microsoft access token available after renewal.');
            }
            sessionStorage.setItem('msal_access_token', tokenResponse.accessToken);
            if (tokenResponse.account) {
              sessionStorage.setItem('username', tokenResponse.account.username);
            }
          }),
          switchMap(() => api === 'iotest' ? appInitService.postProjectsToken$() : of(null)),
          switchMap(() => {
            if (!sessionStorage.getItem(tokenKey)) {
              return throwError(() => new Error('No API access token available after renewal.'));
            }
            return next(authorizeRequest());
          })
        );
      }),
      finalize(() => spinnerService.hide())
    );
  });
};